'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  ArrowUp01Icon,
  ArrowUpRight01Icon,
  FlowConnectionIcon,
  Mail01Icon,
  SearchIcon,
  SmartPhone01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import {
  fmtUsd,
  JOURNEY_CHANNEL_COLOR,
  JOURNEY_CHANNEL_LABEL,
  type Customer,
  type PurchasedProduct,
} from '@/lib/customers/customer-row'

export interface CustomersTableProps {
  customers: Customer[]
  onOpenCustomer: (id: string) => void
  onViewPath: (c: Customer) => void
  pageSize?: number
  productCharLimit?: number
  productMaxItems?: number
}

type SortKey = 'anonId' | 'email' | 'totalSpentCents'
type SortDir = 'asc' | 'desc'
type SortState = {
  key: SortKey | null
  dir: SortDir | null
}

const SORT_LABELS: Record<SortKey, string> = {
  anonId: 'Customer',
  email: 'Identifiers',
  totalSpentCents: 'Spent',
}

function compareValues(a: string | number, b: string | number) {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
}

function nextSort(current: SortState, key: SortKey): SortState {
  if (current.key !== key || !current.dir) return { key, dir: 'desc' }
  if (current.dir === 'desc') return { key, dir: 'asc' }
  return { key: null, dir: null }
}

function SortHeader({
  align = 'left',
  sort,
  sortKey,
  onSort,
}: {
  align?: 'left' | 'right'
  sort: SortState
  sortKey: SortKey
  onSort: (key: SortKey) => void
}) {
  const active = sort.key === sortKey && sort.dir
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`inline-flex items-center gap-1.5 font-medium hover:text-text-strong ${align === 'right' ? 'justify-end text-right' : 'text-left'}`}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span>{SORT_LABELS[sortKey]}</span>
      {active && (
        <HugeiconsIcon
          icon={sort.dir === 'asc' ? ArrowUp01Icon : ArrowDown01Icon}
          className="size-3 text-text-strong"
          aria-hidden
        />
      )}
    </button>
  )
}

function ProductChips({
  products,
  customerId,
  maxItems = 3,
  charLimit = 80,
}: {
  products: PurchasedProduct[]
  customerId: string
  maxItems?: number
  charLimit?: number
}) {
  const router = useRouter()
  const truncated = useMemo(() => {
    let chars = 0
    const visible: PurchasedProduct[] = []
    for (const product of products) {
      if (visible.length >= maxItems || chars + product.name.length > charLimit) break
      visible.push(product)
      chars += product.name.length + 2
    }
    return { visible, hidden: products.length - visible.length }
  }, [charLimit, maxItems, products])

  return (
    <div className="flex flex-wrap gap-1.5">
      {truncated.visible.map((p) => (
        <a
          key={p.name}
        href={p.url ?? '#'}
          target={p.url ? '_blank' : undefined}
          rel={p.url ? 'noreferrer' : undefined}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 rounded-lg border border-black/[0.08] bg-[#FAFAF7] px-2 py-1 text-[11.5px] font-medium text-text-strong hover:border-[#0A8A6E]/40 hover:text-[#0A6b54] dark:bg-canvas dark:hover:text-[#2BC39A]"
        >
          {p.name}
          <HugeiconsIcon icon={ArrowUpRight01Icon} className="size-3 text-[#939294]" />
        </a>
      ))}
      {truncated.hidden > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            router.push(`/customers/${customerId}`)
          }}
          className="inline-flex items-center rounded-lg border border-black/[0.08] bg-white px-2 py-1 text-[11.5px] font-semibold text-[#0A6b54] hover:border-[#0A8A6E]/40 hover:bg-[#0A8A6E]/[0.06] dark:bg-surface dark:text-[#2BC39A]"
        >
          +{truncated.hidden} more
        </button>
      )}
    </div>
  )
}

export function CustomersTable({
  customers,
  onOpenCustomer,
  onViewPath,
  pageSize = 50,
  productCharLimit = 80,
  productMaxItems = 3,
}: CustomersTableProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sort, setSort] = useState<SortState>({ key: null, dir: null })
  const [visibleCount, setVisibleCount] = useState(pageSize)
  const sentinelRef = useRef<HTMLTableRowElement | null>(null)

  const filteredCustomers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return customers

    return customers.filter((customer) => {
      const searchable = [
        customer.anonId,
        customer.email ?? '',
        customer.phone ?? '',
        ...customer.products.map((product) => product.name),
      ].join(' ').toLowerCase()
      return searchable.includes(query)
    })
  }, [customers, searchTerm])

  const sortedCustomers = useMemo(() => {
    if (!sort.key || !sort.dir) return filteredCustomers

    return [...filteredCustomers].sort((a, b) => {
      const compared = compareValues(a[sort.key!] ?? '', b[sort.key!] ?? '')
      return sort.dir === 'asc' ? compared : -compared
    })
  }, [filteredCustomers, sort])

  const visibleCustomers = useMemo(
    () => sortedCustomers.slice(0, visibleCount),
    [sortedCustomers, visibleCount],
  )
  const fullyRendered = visibleCustomers.length >= sortedCustomers.length

  useEffect(() => {
    setVisibleCount(pageSize)
  }, [pageSize, searchTerm, sort.key, sort.dir])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || fullyRendered) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        setVisibleCount((current) => Math.min(current + pageSize, sortedCustomers.length))
      },
      { rootMargin: '240px 0px' },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [fullyRendered, pageSize, sortedCustomers.length, visibleCustomers.length])

  const handleSort = (key: SortKey) => {
    setSort((current) => nextSort(current, key))
  }

  return (
    <>
      <div className="mt-4">
        <label className="flex w-full items-center gap-2.5 rounded-lg border border-atrium-border bg-atrium-inner px-3 py-2.5 md:max-w-[520px]">
          <HugeiconsIcon icon={SearchIcon} className="size-4 text-atrium-black/40" aria-hidden />
          <input
            type="search"
            placeholder="Search by ID, email, phone, or product..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            className="min-w-0 flex-1 bg-transparent text-sm text-atrium-black outline-none placeholder:text-atrium-black/40"
            aria-label="Search customers"
          />
        </label>
      </div>

      {/* Desktop table */}
      <div className="relative mt-4 hidden overflow-hidden rounded-2xl border border-black/[0.06] bg-white dark:border-white/[0.08] dark:bg-surface md:block">
        <table className="w-full border-collapse text-[13px]">
          <thead className="sticky top-0 z-20 bg-white dark:bg-surface">
            <tr className="border-b border-black/[0.06] text-left font-mono text-[11px] uppercase tracking-[0.06em] text-[#939294] dark:border-white/[0.08]">
              <th className="px-4 py-3 font-medium">
                <SortHeader sort={sort} sortKey="anonId" onSort={handleSort} />
              </th>
              <th className="px-4 py-3 font-medium">
                <SortHeader sort={sort} sortKey="email" onSort={handleSort} />
              </th>
              <th className="px-4 py-3 font-medium">Products purchased</th>
              <th className="px-4 py-3 font-medium">Path to purchase</th>
              <th className="px-4 py-3 text-right font-medium">
                <SortHeader align="right" sort={sort} sortKey="totalSpentCents" onSort={handleSort} />
              </th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {visibleCustomers.map((c) => (
              <tr
                key={c.id}
                onClick={() => onOpenCustomer(c.id)}
                className="cursor-pointer border-b border-black/[0.04] transition-colors last:border-0 hover:bg-[#0A8A6E]/[0.04] dark:border-white/[0.06] dark:hover:bg-[#2BC39A]/[0.06]"
              >
                <td className="px-4 py-3.5 align-top">
                  <div className="font-mono text-[12.5px] font-bold text-text-strong">{c.anonId}</div>
                </td>
                <td className="px-4 py-3.5 align-top">
                  <div className="flex items-center gap-1.5 text-[12.5px] text-text-strong">
                    <HugeiconsIcon icon={Mail01Icon} className="size-3.5 text-[#939294]" />
                    {c.email ?? 'No email'}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-[12px] text-text">
                    <HugeiconsIcon icon={SmartPhone01Icon} className="size-3.5 text-[#939294]" />
                    {c.phone ?? 'No phone'}
                  </div>
                </td>
                <td className="px-4 py-3.5 align-top">
                  <ProductChips
                    products={c.products}
                    customerId={c.id}
                    maxItems={productMaxItems}
                    charLimit={productCharLimit}
                  />
                </td>
                <td className="px-4 py-3.5 align-top">
                  <div className="flex items-center gap-2">
                    <span className="flex items-center -space-x-1">
                      {c.channels.map((ch) => (
                        <span
                          key={ch}
                          title={JOURNEY_CHANNEL_LABEL[ch]}
                          className="size-3.5 rounded-full ring-2 ring-white dark:ring-surface"
                          style={{ background: JOURNEY_CHANNEL_COLOR[ch] }}
                        />
                      ))}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onViewPath(c)
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-black/[0.08] bg-white px-2 py-1 text-[11.5px] font-semibold text-[#0A6b54] hover:border-[#0A8A6E]/40 hover:bg-[#0A8A6E]/[0.06] dark:bg-surface dark:text-[#2BC39A]"
                    >
                      <HugeiconsIcon icon={FlowConnectionIcon} className="size-3.5" />
                      View path
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3.5 text-right align-top">
                  <div className="font-mono text-[13px] font-bold text-text-strong">{fmtUsd(c.totalSpentCents)}</div>
                  <div className="mt-0.5 text-[11px] text-[#939294]">{c.ordersCount} order{c.ordersCount === 1 ? '' : 's'}</div>
                </td>
                <td className="px-3 py-3.5 text-right align-middle">
                  <HugeiconsIcon icon={ArrowRight01Icon} className="size-4 text-[#c4c2ba]" />
                </td>
              </tr>
            ))}
            {sortedCustomers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-text">
                  No customers match your search.
                </td>
              </tr>
            ) : fullyRendered ? (
              <tr>
                <td colSpan={6} className="px-4 py-4 text-center font-mono text-[11px] uppercase tracking-[0.06em] text-[#939294]">
                  End of results - {sortedCustomers.length.toLocaleString()} customers
                </td>
              </tr>
            ) : (
              <tr ref={sentinelRef} aria-hidden>
                <td colSpan={6} className="h-10" />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mt-4 space-y-2.5 md:hidden">
        {sortedCustomers.length === 0 ? (
          <div className="rounded-2xl border border-black/[0.06] bg-white p-4 text-center text-sm text-text dark:border-white/[0.08] dark:bg-surface">
            No customers match your search.
          </div>
        ) : (
          sortedCustomers.map((c) => (
            <div
              key={c.id}
              onClick={() => onOpenCustomer(c.id)}
              className="cursor-pointer rounded-2xl border border-black/[0.06] bg-white p-4 dark:border-white/[0.08] dark:bg-surface"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-mono text-[13px] font-bold text-text-strong">{c.anonId}</div>
                <div className="text-right">
                  <div className="font-mono text-[14px] font-bold text-text-strong">{fmtUsd(c.totalSpentCents)}</div>
                  <div className="text-[11px] text-[#939294]">{c.ordersCount} order{c.ordersCount === 1 ? '' : 's'}</div>
                </div>
              </div>
              <div className="mt-2.5 space-y-1">
                <div className="flex items-center gap-1.5 text-[12.5px] text-text-strong">
                  <HugeiconsIcon icon={Mail01Icon} className="size-3.5 text-[#939294]" />
                  {c.email ?? 'No email'}
                </div>
                <div className="flex items-center gap-1.5 text-[12px] text-text">
                  <HugeiconsIcon icon={SmartPhone01Icon} className="size-3.5 text-[#939294]" />
                  {c.phone ?? 'No phone'}
                </div>
              </div>
              <div className="mt-2.5">
                <ProductChips
                  products={c.products}
                  customerId={c.id}
                  maxItems={productMaxItems}
                  charLimit={productCharLimit}
                />
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-black/[0.06] pt-3 dark:border-white/[0.08]">
                <span className="flex items-center -space-x-1">
                  {c.channels.map((ch) => (
                    <span
                      key={ch}
                      className="size-3.5 rounded-full ring-2 ring-white dark:ring-surface"
                      style={{ background: JOURNEY_CHANNEL_COLOR[ch] }}
                    />
                  ))}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onViewPath(c)
                  }}
                  className="inline-flex items-center gap-1 rounded-lg border border-black/[0.08] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#0A6b54] dark:bg-surface dark:text-[#2BC39A]"
                >
                  <HugeiconsIcon icon={FlowConnectionIcon} className="size-3.5" />
                  View path
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  )
}
